import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { MessageSquare, Users, HelpCircle, Calendar, Sparkles, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getUserTier, UserTier } from "@/lib/tierUtils";
import { useAuth } from "@/contexts/AuthContext";

interface TaskHelpDialogProps {
  isOpen: boolean;
  onClose: () => void;
  taskTitle: string;
  taskId: string;
  serviceOptions?: string[];
}

export const TaskHelpDialog = ({ isOpen, onClose, taskTitle, taskId, serviceOptions = [] }: TaskHelpDialogProps) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: userTier = 'DIY' as UserTier } = useQuery({
    queryKey: ['user-tier', user?.id],
    queryFn: () => getUserTier(user!.id),
    enabled: !!user?.id
  });

  const handleAskAI = () => {
    // Navigate to Legal Paragon AI with task context
    navigate(`/support?ai=true&context=task-${taskId}&question=${encodeURIComponent(`Help with: ${taskTitle}`)}`);
    onClose();
  };

  const handleCommunity = () => {
    navigate('/community');
    onClose();
  };

  const handleSupport = () => {
    navigate('/support');
    onClose();
  };

  const getDIYOptions = () => (
    <div className="space-y-3">
      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleAskAI}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Ask Legal Paragon AI</CardTitle>
              <CardDescription className="text-sm">
                Get instant AI-powered guidance about this task
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleCommunity}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Search Community Discussions</CardTitle>
              <CardDescription className="text-sm">
                See how other members approached this task
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleSupport}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <HelpCircle className="h-5 w-5 text-orange-500" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Submit Support Ticket</CardTitle>
              <CardDescription className="text-sm">
                Get help from our support team
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );

  const getGuidedOptions = () => (
    <div className="space-y-3">
      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleAskAI}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Ask Legal Paragon AI</CardTitle>
              <CardDescription className="text-sm">
                Get instant AI-powered guidance
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleCommunity}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Post in Community</CardTitle>
              <CardDescription className="text-sm">
                Discuss with other members
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleSupport}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-orange-500/10 rounded-lg">
              <MessageSquare className="h-5 w-5 text-orange-500" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Contact Support Team</CardTitle>
              <CardDescription className="text-sm">
                Direct access to support specialists
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Schedule Guidance Call</CardTitle>
              <CardDescription className="text-sm">
                Included with your Guided Path subscription
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Button className="w-full" size="sm">
            Book a Call
          </Button>
        </CardContent>
      </Card>
    </div>
  );

  const getPremiumOptions = () => (
    <div className="space-y-3">
      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleAskAI}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Ask Legal Paragon AI</CardTitle>
              <CardDescription className="text-sm">
                Instant AI-powered guidance
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>

      <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/30">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <MessageSquare className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base flex items-center gap-2">
                Contact Dedicated Support
                <span className="text-xs bg-purple-500/20 text-purple-700 px-2 py-0.5 rounded-full">Premium</span>
              </CardTitle>
              <CardDescription className="text-sm">
                Priority response from your dedicated support team
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Button className="w-full bg-purple-600 hover:bg-purple-700" size="sm">
            Contact Now
          </Button>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Calendar className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Priority Scheduling</CardTitle>
              <CardDescription className="text-sm">
                Unlimited planning support included
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <Button className="w-full" size="sm">
            Schedule Support Call
          </Button>
        </CardContent>
      </Card>

      <Card className="hover:border-primary transition-colors cursor-pointer" onClick={handleCommunity}>
        <CardHeader className="pb-3">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-500/10 rounded-lg">
              <Users className="h-5 w-5 text-blue-500" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-base">Access Premium Community</CardTitle>
              <CardDescription className="text-sm">
                Connect with other premium members
              </CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
    </div>
  );

  const renderHelpOptions = () => {
    switch (userTier) {
      case "Premium":
        return getPremiumOptions();
      case "Guided":
        return getGuidedOptions();
      case "DIY":
      default:
        return getDIYOptions();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Get Help with This Task</DialogTitle>
          <DialogDescription>
            Choose how you'd like to get assistance with "{taskTitle}"
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Main Help Options */}
          <div>
            <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase">
              Support Options {userTier !== "DIY" && `(${userTier} Path)`}
            </h3>
            {renderHelpOptions()}
          </div>

          {/* Service Options if available */}
          {serviceOptions.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold mb-3 text-muted-foreground uppercase">
                Done-For-You Services
              </h3>
              <div className="space-y-2">
                {serviceOptions.map((service, index) => (
                  <Card key={index} className="bg-gradient-to-br from-green-500/5 to-green-600/5 border-green-500/20">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-base">{service}</CardTitle>
                          <CardDescription className="text-sm">
                            Let our team handle this for you
                          </CardDescription>
                        </div>
                        <ExternalLink className="h-4 w-4 text-muted-foreground" />
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <Button variant="outline" size="sm" className="w-full">
                        Learn More
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Tier Upgrade CTA (for DIY users) */}
          {userTier === "DIY" && (
            <Card className="bg-gradient-to-br from-primary/5 to-blue-500/5 border-primary/20">
              <CardHeader>
                <CardTitle className="text-base">Need More Support?</CardTitle>
                <CardDescription>
                  Upgrade to Guided or Premium Path for:
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm mb-4">
                  <li className="flex items-start gap-2">
                    <span className="text-primary">✓</span>
                    <span>Planning calls with UNA specialists</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">✓</span>
                    <span>Priority support response times</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary">✓</span>
                    <span>Facilitated board meetings</span>
                  </li>
                </ul>
                <Button className="w-full" size="sm">
                  View Upgrade Options
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
